"use client";

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar } from '../components/ui/avatar';
import { IconButton } from '../components/ui/icon-button';
import { ToggleGroup } from '../components/ui/toggle-group';
import { DropdownMenu } from '../components/ui/dropdown-menu';
import { IconWithBackground } from '../components/ui/icon-with-background';
import { getBirdsviewData, getWorkPlanSummary, getMemberTasks } from '../services/managerService';
import { 
    FeatherArrowLeft, 
    FeatherPlus, 
    FeatherDownload, 
    FeatherMoreVertical,
    FeatherCheckCircle, 
    FeatherClock, 
    FeatherFlag, 
    FeatherFile,
    FeatherEdit2, 
    FeatherAlertTriangle, 
    FeatherXCircle
} from '../components/ui/icons';
import { getTodayDate, formatDateForComparison, isDateInPast, doesWorkPlanNeedAttention } from '../utils/date';
import './BirdsviewDashboard.css';

const getStatusVariant = (status, hasUnclosedPastPlan) => {
    if (hasUnclosedPastPlan) return 'error';
    
    switch (status.toLowerCase()) {
        case 'at-risk':
            return 'error';
        case 'not-created':
            return 'neutral';
        case 'created':
        case 'in progress':
            return 'warning';
        case 'completed':
        case 'submitted':
            return 'success';
        default:
            return 'default';
    }
};

const getStatusColor = (status, hasUnclosedPastPlan) => {
    if (hasUnclosedPastPlan) {
        return {
            border: 'border-error-100',
            bg: 'bg-error-50',
            text: 'text-error-900',
            badge: 'bg-error-100'
        };
    }

    switch (status.toLowerCase()) {
        case 'at-risk':
            return {
                border: 'border-error-100',
                bg: 'bg-error-50',
                text: 'text-error-900',
                badge: 'bg-error-100'
            };
        case 'not-created':
            return {
                border: 'border-neutral-200',
                bg: 'bg-neutral-50',
                text: 'text-neutral-900',
                badge: 'bg-neutral-100'
            };
        case 'created':
        case 'in progress':
            return {
                border: 'border-warning-100',
                bg: 'bg-warning-50',
                text: 'text-warning-900',
                badge: 'bg-warning-100'
            };
        case 'completed':
        case 'submitted':
            return {
                border: 'border-neutral-border',
                bg: 'bg-neutral-50',
                text: 'text-neutral-900',
                badge: 'bg-neutral-100'
            };
        default:
            return {
                border: 'border-neutral-border',
                bg: 'bg-default-background',
                text: 'text-default-font',
                badge: 'bg-neutral-100'
            };
    }
};

const BirdsviewDashboard = () => {
    const [teamData, setTeamData] = useState([]);
    const [memberTasks, setMemberTasks] = useState({});
    const [tasksLoading, setTasksLoading] = useState({});
    const [tasksError, setTasksError] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [showSummary, setShowSummary] = useState(false);
    const [selectedSummary, setSelectedSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTeamData = async () => {
            try {
                setLoading(true);
                const response = await getBirdsviewData();
                
                // Process the response to add attention flags
                const processedTeamData = response.map(member => {
                    const today = getTodayDate();
                    const hasUnclosedPastPlan = member.work_plans?.some(plan => {
                        const planDate = formatDateForComparison(plan.created_at);
                        return planDate < today && !plan.is_closed;
                    });

                    return {
                        ...member,
                        hasUnclosedPastPlan,
                        needsAttention: !member.hasTodaysPlan || hasUnclosedPastPlan
                    };
                });

                setTeamData(processedTeamData);
                
                // Fetch tasks for members who have plans
                const membersWithPlans = processedTeamData.filter(member => member.hasTodaysPlan && member.taskCount > 0);
                
                // Initialize loading and error states for each member
                const loadingStates = {};
                const errorStates = {};
                membersWithPlans.forEach(member => {
                    loadingStates[member.id] = true;
                    errorStates[member.id] = null;
                });
                setTasksLoading(loadingStates);
                setTasksError(errorStates);

                // Fetch tasks for each member individually to handle errors better
                for (const member of membersWithPlans) {
                    try {
                        const tasks = await getMemberTasks(member.id);
                        setMemberTasks(prev => ({
                            ...prev,
                            [member.id]: tasks
                        }));
                        setTasksError(prev => ({
                            ...prev,
                            [member.id]: null
                        }));
                    } catch (err) {
                        console.error(`Error fetching tasks for member ${member.id}:`, err);
                        setTasksError(prev => ({
                            ...prev,
                            [member.id]: 'Failed to load tasks'
                        }));
                    } finally {
                        setTasksLoading(prev => ({
                            ...prev,
                            [member.id]: false
                        }));
                    }
                }
                
                setError(null);
            } catch (err) {
                setError('Failed to load team data');
                console.error('Error fetching team data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchTeamData();
    }, []);

    const handleViewSummary = async (userId, planId) => {
        if (!planId) return;
        
        try {
            setSummaryLoading(true);
            const today = new Date().toISOString().split('T')[0];
            const summary = await getWorkPlanSummary(userId, today);
            setSelectedSummary(summary);
            setShowSummary(true);
        } catch (err) {
            console.error('Error loading summary:', err);
            setError('Failed to load work plan summary');
        } finally {
            setSummaryLoading(false);
        }
    };

    const filteredTeam = teamData.filter(member => {
        console.log('Member data:', member);
        if (filter === 'all') return true;
        return member.status.toLowerCase() === filter.toLowerCase();
    });

    if (loading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <div className="loading">Loading dashboard...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <div className="alert alert-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            {/* Header section */}
            <div className="flex w-full flex-col items-start gap-6">
                <div className="flex w-full items-center gap-4">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={() => navigate('/dashboard')}
                    >
                        Back
                    </Button>
                    <span className="grow text-heading-2 font-heading-2 text-default-font">
                        Team Work Plans Overview
                    </span>
                    <div className="flex items-center gap-4">
                        <Button
                            variant="brand-secondary"
                            icon={<FeatherPlus />}
                        >
                            Add member
                        </Button>
                        <Button icon={<FeatherDownload />}>
                            Export
                        </Button>
                    </div>
                </div>
                <ToggleGroup value={filter} onValueChange={setFilter}>
                    <ToggleGroup.Item value="all">All</ToggleGroup.Item>
                    <ToggleGroup.Item value="at-risk">At Risk</ToggleGroup.Item>
                    <ToggleGroup.Item value="not-created">Not Created</ToggleGroup.Item>
                    <ToggleGroup.Item value="in-progress">In Progress</ToggleGroup.Item>
                    <ToggleGroup.Item value="completed">Completed</ToggleGroup.Item>
                </ToggleGroup>
            </div>

            {/* Team Member Grid */}
            <div className="task-grid">
                {filteredTeam.map(member => {
                    const statusColors = getStatusColor(member.status, member.hasUnclosedPastPlan);
                    const statusVariant = getStatusVariant(member.status, member.hasUnclosedPastPlan);

                    return (
                        <div 
                            key={member.id}
                            className={`task-card ${member.needsAttention ? 'needs-attention' : ''}`}
                        >
                            {/* Member Header */}
                            <div className="flex w-full items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <Avatar size="large">
                                        {member.name.charAt(0)}
                                    </Avatar>
                                    <div className="flex flex-col items-start">
                                        <span className="text-heading-3 font-heading-3 text-default-font">
                                            {member.name}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <Badge variant={statusVariant}>
                                                {member.status}
                                            </Badge>
                                            {member.hasUnclosedPastPlan && (
                                                <Badge variant="error" icon={<FeatherAlertTriangle />}>
                                                    Unclosed Past Plan
                                                </Badge>
                                            )}
                                            {!member.hasTodaysPlan && (
                                                <Badge variant="warning" icon={<FeatherAlertTriangle />}>
                                                    No Today's Plan
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Work Plan Stats */}
                            <div className={`flex w-full flex-col md:flex-row items-start md:items-center justify-between rounded-md ${statusColors.badge} p-4 gap-4 md:gap-0 ${member.needsAttention ? 'border-2 border-error-500' : ''}`}>
                                <div className="flex flex-col items-start gap-1">
                                    <span className={`text-caption font-caption ${statusColors.text}`}>
                                        Today's Plan
                                    </span>
                                    <span className={`text-body-bold font-body-bold ${statusColors.text}`}>
                                        {member.hasTodaysPlan ? 'Created' : 'Not Created'}
                                    </span>
                                </div>
                                <div className="flex flex-col items-start gap-1">
                                    <span className={`text-caption font-caption ${statusColors.text}`}>
                                        Tasks
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <IconWithBackground
                                            variant={statusVariant}
                                            icon={member.taskCount > 0 ? <FeatherCheckCircle /> : <FeatherXCircle />}
                                        />
                                        <span className={`text-body-bold font-body-bold ${statusColors.text}`}>
                                            {member.taskCount}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-col items-start gap-1">
                                    <span className={`text-caption font-caption ${statusColors.text}`}>
                                        Hours Logged
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <IconWithBackground
                                            variant={member.hoursLogged === 0 ? 'warning' : 'default'}
                                            icon={<FeatherClock />}
                                        />
                                        <span className={`text-body-bold font-body-bold ${statusColors.text}`}>
                                            {member.hoursLogged}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Task List */}
                            <div className="flex w-full flex-col items-start gap-4">
                                <div className="flex w-full items-center justify-between">
                                    <span className="text-body-bold font-body-bold text-default-font">
                                        Planned Tasks
                                    </span>
                                    <Badge 
                                        variant={statusVariant}
                                        icon={<FeatherFlag />}
                                    >
                                        {member.taskCount}
                                    </Badge>
                                </div>
                                <div className="flex w-full flex-col items-start gap-2">
                                    {member.taskCount > 0 ? (
                                        tasksLoading[member.id] ? (
                                            <div className="flex w-full items-center gap-2 rounded-md bg-neutral-50 px-3 py-2">
                                                <span className="text-body font-body text-subtext-color">
                                                    Loading tasks...
                                                </span>
                                            </div>
                                        ) : tasksError[member.id] ? (
                                            <div className="flex w-full items-center gap-2 rounded-md bg-error-50 px-3 py-2">
                                                <IconWithBackground
                                                    variant="error"
                                                    icon={<FeatherAlertTriangle />}
                                                />
                                                <span className="grow shrink-0 basis-0 text-body font-body text-error-900">
                                                    {tasksError[member.id]}
                                                </span>
                                            </div>
                                        ) : memberTasks[member.id]?.map((task, index) => (
                                            <div key={index} className="flex w-full items-center gap-2 rounded-md bg-neutral-50 px-3 py-2">
                                                <IconWithBackground icon={<FeatherFile />} />
                                                <span className="grow shrink-0 basis-0 text-body font-body text-default-font">
                                                    {task.title}
                                                </span>
                                                <Badge variant="neutral">{task.hours} Hours</Badge>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="flex w-full items-center gap-2 rounded-md bg-error-50 px-3 py-2">
                                            <IconWithBackground
                                                variant="error"
                                                icon={<FeatherAlertTriangle />}
                                            />
                                            <span className="grow shrink-0 basis-0 text-body font-body text-error-900">
                                                No tasks planned
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Summary Modal */}
            {showSummary && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Work Plan Summary</h3>
                            <Button variant="neutral" onClick={() => setShowSummary(false)}>Close</Button>
                        </div>
                        <div className="modal-body">
                            {summaryLoading ? (
                                <div className="text-center">Loading summary...</div>
                            ) : selectedSummary ? (
                                <div className="summary-content">
                                    <h4>{selectedSummary.userName}'s Work Plan</h4>
                                    <div className="summary-stats">
                                        <div>
                                            <span className="stat-title">Status</span>
                                            <div className="stat-value">{selectedSummary.status}</div>
                                        </div>
                                        <div>
                                            <span className="stat-title">Total Hours</span>
                                            <div className="stat-value">{selectedSummary.totalHours}</div>
                                        </div>
                                    </div>
                                    <div className="task-list">
                                        {selectedSummary.tasks.map(task => (
                                            <div key={task.id} className="task-item">
                                                <div className="task-item-header">
                                                    <h5>{task.title}</h5>
                                                    <Badge variant={task.status === 'at-risk' ? 'error' : 'default'}>
                                                        {task.status}
                                                    </Badge>
                                                </div>
                                                {task.description && (
                                                    <p className="task-description">{task.description}</p>
                                                )}
                                                <div className="task-meta">
                                                    <span>Hours: {task.hours}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div>No summary data available</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BirdsviewDashboard; 